import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Card } from '#pie/components/card/Card';
import { ConnectionInfo } from '#pie/components/connection-info/ConnectionInfo';
import { Divider } from '#pie/components/divider/Divider';
import { EanFailure } from '#pie/components/ean-failure/EanFailure';
import { Message } from '#pie/components/message/Message';
import { Page, PageHeader } from '#pie/components/page/Page';
import { RelatedKnowledgeArticlesCard } from '#pie/components/related-knowledge-articles-card/RelatedKnowledgeArticlesCard';
import { Skeleton } from '#pie/components/skeleton/Skeleton';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { useRelatedKnowledgeArticles } from '#pie/hooks/useRelatedKnowlegdeArticles';
import {
  UsageClassification,
  useGetConnectionInformation,
  useGetMainCategory,
} from '#pie/internal/api/pie-bff';
import { isValidXmlCharacters } from '#pie/utils/isValidXmlCharacters';
import { typedFormFields } from '#pie/utils/typedFormFields';
import { useTicketCategoryIdKV } from '../../../hooks/useTicketCategoryIdKV';
import {
  assigneeValidationSchema,
  TicketAssigneeCard,
} from '../TicketAssigneeCard';
import {
  attachmentValidationSchema,
  TicketAttachmentFields,
} from '../TicketAttachmentFields';
import {
  categoriesValidationSchema,
  TicketCategoriesFields,
} from '../TicketCategoriesFields';
import { TicketConnectionCard } from '../TicketConnectionCard';
import { TicketDescription } from '../TicketDescription';
import {
  gridOperatorValidationSchema,
  TicketGridOperatorsFields,
} from '../TicketGridOperatorsFields';
import { TicketTemplateDialog } from '../TicketTemplateDialog';
import { useTemplate } from '../useTemplate';
import type { StepProps } from './TicketCreateEan';
import { useEanFormContext } from './TicketCreateEan';

export const detailsSchema = z.object({
  ...gridOperatorValidationSchema.shape,
  ...categoriesValidationSchema.shape,
  ...assigneeValidationSchema.shape,
  ...attachmentValidationSchema.shape,
  description: z
    .string()
    .max(2000)
    .optional()
    .refine((value) => isValidXmlCharacters(value), {
      error: () => ({
        message: 'zod.errors.invalid_xml_characters',
      }),
    }),
  eventDate: z.string().optional(),
  title: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[\p{L}\p{N} _\-.]+$/u, {
      message: 'zod.errors.invalid_characters',
    }),
});

type DetailsForm = z.infer<typeof detailsSchema>;

const { FormDate, FormText } = typedFormFields<DetailsForm>();

export const TicketCreateEanDetails = ({
  wizardActions,
  onSubmit,
  onBack,
  stepper,
}: StepProps) => {
  const { t } = useTranslation();
  const { t: transtic } = useTranslation('ticket');

  const { data, setStepShouldBlock } = useEanFormContext();

  const { data: elecData, isLoading: isElecLoading } =
    useGetConnectionInformation(
      { ean18: data.electricityEan! },
      { query: { enabled: !!data.electricityEan } },
    );
  const { data: gasData, isLoading: isGasLoading } =
    useGetConnectionInformation(
      { ean18: data.gasEan! },
      { query: { enabled: !!data.gasEan } },
    );
  const { data: categoryData } = useGetMainCategory();

  const categoryIdKV = useTicketCategoryIdKV();
  const categoryItems = categoryData?.items ?? [];
  const regionalGridOperatorId =
    elecData?.regionalGridOperator?.id ??
    gasData?.regionalGridOperator?.id ??
    data.regionalGridOperatorId;
  const isDuoGridArea = Boolean(
    elecData?.regionalGridOperator?.id &&
      gasData?.regionalGridOperator?.id &&
      elecData.regionalGridOperator.id !== gasData.regionalGridOperator.id,
  );

  const validationSchema = detailsSchema.superRefine((check, ctx) => {
    if (check.mainCategoryId === categoryIdKV && !check.eventDate) {
      ctx.addIssue({
        code: 'custom',
        message: t('zod.errors.invalid_event_date'),
        path: ['eventDate'],
      });
    }

    const attachmentRequired = categoryItems
      .find(({ id }: { id: string }) => id === check.mainCategoryId)
      ?.subCategories?.find(
        ({ id }: { id: string }) => id === check.subCategoryId,
      )?.attachmentRequired;

    if (attachmentRequired && !check.files?.length) {
      ctx.addIssue({
        code: 'custom',
        message: transtic('create.attachments.required_for_subcategory'),
        path: ['files'],
      });
    }
  });

  const formMethods = useForm<DetailsForm>({
    defaultValues: {
      ...data,
      regionalGridOperatorId,
      title: '',
      mainCategoryId: '',
      subCategoryId: '',
      description: '',
      files: [],
      eventDate: undefined,
    },
    resolver: zodResolver(validationSchema),
  });

  const {
    handleSubmit,
    watch,
    clearErrors,
    getValues,
    setError,
    setValue,
    reset,
    formState: { isDirty },
  } = formMethods;

  const category = watch('mainCategoryId');
  const subCategory = watch('subCategoryId');
  const files = watch('files');
  const [isAttachmentRequired, setIsAttachmentRequired] = useState(false);

  const shouldShowEventDate = category === categoryIdKV;

  useEffect(() => {
    if (regionalGridOperatorId) {
      setValue('regionalGridOperatorId', regionalGridOperatorId);
    }
  }, [regionalGridOperatorId, setValue]);

  const { showConfirm, onConfirmClick, closeConfirm } = useTemplate<
    typeof detailsSchema
  >({
    getValues,
    setValue,
    watch,
  });

  useEffect(() => {
    if (!shouldShowEventDate) {
      reset({
        ...getValues(),
        eventDate: undefined,
      });
    }
  }, [getValues, reset, shouldShowEventDate]);

  useEffect(() => {
    if (isDirty) {
      setStepShouldBlock(true);
    }
  }, [isDirty]);

  useEffect(() => {
    if (!isAttachmentRequired || (files?.length ?? 0) > 0) {
      clearErrors('files');
    }
  }, [clearErrors, files?.length, isAttachmentRequired]);

  const onSubmitDetails: SubmitHandler<DetailsForm> = (values) => {
    if (isAttachmentRequired && (!values.files || values.files.length === 0)) {
      setError('files', {
        type: 'required',
        message: transtic('create.attachments.required_for_subcategory'),
      });
      return;
    }

    onSubmit({
      ...values,
      duoGridArea: isDuoGridArea
        ? {
            electricityGridOperatorId: elecData!.regionalGridOperator!.id,
            gasGridOperatorId: gasData!.regionalGridOperator!.id,
          }
        : undefined,
    });
  };

  const { relatedArticles, relatedArticlesLoading } =
    useRelatedKnowledgeArticles(category, subCategory);

  const gasUsage = gasData?.usageClassification ?? UsageClassification.None;
  const elecUsage = elecData?.usageClassification ?? UsageClassification.None;

  const usageClassification = !gasData
    ? elecUsage
    : !elecData
      ? gasUsage
      : gasUsage === elecUsage
        ? elecUsage
        : UsageClassification.None;

  return (
    <Page>
      <PageHeader
        title={transtic('create.ean.page_title')}
        backOnClick={onBack}
        previousText={t('common.steps.previous')}
        details={stepper}
      />
      <TicketTemplateDialog
        closeConfirm={closeConfirm}
        onConfirmClick={onConfirmClick}
        showConfirm={showConfirm}
      />
      <FormProvider {...formMethods}>
        <form
          className="flex flex-col items-start gap-6 pb-8 md:flex-row"
          onSubmit={handleSubmit(onSubmitDetails)}
        >
          <Card className="flex-2 p-6">
            <Stack gap="lg">
              {(!!elecData?.failureEventCode ||
                !!gasData?.failureEventCode) && (
                <EanFailure
                  electricityEan={data.electricityEan}
                  gasEan={data.gasEan}
                />
              )}
              <Stack gap="md">
                <Text as="h2" variant="h5">
                  {transtic('create.heading.ticket_about')}
                </Text>
                <TicketGridOperatorsFields
                  disabled={!!regionalGridOperatorId}
                  selectedOption={
                    isDuoGridArea
                      ? {
                          label: `${elecData!.regionalGridOperator!.name}, ${gasData!.regionalGridOperator!.name}`,
                          value: regionalGridOperatorId!,
                        }
                      : undefined
                  }
                />
                {isDuoGridArea && (
                  <Message
                    variant="warning"
                    className="-mt-3 bg-transparent p-0"
                  >
                    <Text>{transtic('create.duo_grid_area.message')}</Text>
                  </Message>
                )}
                <TicketCategoriesFields
                  usageClassification={usageClassification}
                  onAttachmentRequiredChange={setIsAttachmentRequired}
                />
                {shouldShowEventDate && (
                  <FormDate
                    label={t('common.event_date')}
                    fullWidth
                    name="eventDate"
                    rules={{
                      required: true,
                    }}
                    disableFutureDates
                  />
                )}
              </Stack>
              <Stack gap="md">
                <Text as="h2" variant="h5">
                  {transtic('create.heading.details')}
                </Text>
                <FormText
                  label={t('common.subject')}
                  fullWidth
                  name="title"
                  rules={{ maxLength: 100, required: true }}
                  showWordCount
                />
                <TicketDescription />
                {isAttachmentRequired && (!files || files.length === 0) && (
                  <Message variant="warning">
                    <Text>
                      {transtic('create.attachments.required_for_subcategory')}
                    </Text>
                  </Message>
                )}
                <TicketAttachmentFields />
              </Stack>
              <Divider />
              <Stack direction="row" className="justify-between" gap="lg">
                {wizardActions}
              </Stack>
            </Stack>
          </Card>
          <Stack as="aside" gap="lg" className="flex-1">
            <TicketAssigneeCard />
            <TicketConnectionCard
              electricityEan={data.electricityEan}
              gasEan={data.gasEan}
            />
            {data?.electricityEan &&
              (isElecLoading ? (
                <Skeleton />
              ) : (
                elecData && <ConnectionInfo ean18={data.electricityEan} />
              ))}
            {data?.gasEan &&
              (isGasLoading ? (
                <Skeleton className="h-14 rounded-md" />
              ) : (
                gasData && <ConnectionInfo ean18={data.gasEan} />
              ))}
            {category && (
              <RelatedKnowledgeArticlesCard
                articles={relatedArticles}
                isLoading={relatedArticlesLoading}
              />
            )}
          </Stack>
        </form>
      </FormProvider>
    </Page>
  );
};
